/** A2A → Topics: topic domains (who may collaborate, with what authority, seeing what). */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/ui';
import TopicWizard from '../../components/a2a/TopicWizard';
import ActorSwitch from '../../components/a2a/ActorSwitch';
import { a2a, statusBadge, Topic } from '../../lib/a2aApi';
import { usePermission } from '../../hooks/usePermission';
import '../../components/a2a/a2a.css';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../../i18n/format';

const A2ATopics: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t: tr } = useTranslation();
  const nav = useNavigate();
  const canWrite = usePermission('update:policy');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [ov, setOv] = useState<any>(null);
  const [wizard, setWizard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { const [t, o] = await Promise.all([a2a.topics(apiUrl), a2a.overview(apiUrl)]); setTopics(t.topics); setOv(o); setError(null); }
    catch (e: any) { setError(e.message); }
  }, [apiUrl]);
  useEffect(() => { void load(); }, [load]);

  const approve = async (t: Topic) => {
    try { await a2a.approve(apiUrl, t.topic_id); setNote(`${t.name} is now active.`); setError(null); await load(); }
    catch (e: any) { setError(e.message); setNote(null); }
  };

  return (
    <div className="page-content">
      <div className="page-title">
        <div><h1>{tr('pages.a2a.a2ATopics.topics')}</h1><p className="subtitle">{tr('pages.a2a.a2ATopics.topicDomainsWhichAgentsCollaborateWho')}</p></div>
        <div className="a2a-row"><ActorSwitch apiUrl={apiUrl} onChange={load} />{canWrite && <button className="ent-btn primary" onClick={() => setWizard(true)}><Icon name="plus" /> {tr('pages.a2a.a2ATopics.newTopic')}</button>}</div>
      </div>
      {error && <div className="notice error"><Icon name="warning" /> {error}</div>}
      {note && <div className="notice success"><Icon name="checkCircle" /> {note}</div>}

      {ov && <div className="a2a-tiles">
        <div className="a2a-tile"><div className="k">{tr('pages.a2a.a2ATopics.topics')}</div><div className="v">{ov.topics.total}</div><div className="s">{tr('pages.a2a.a2ATopics.active', { v0: ov.topics.active })}</div></div>
        <div className="a2a-tile"><div className="k">{tr('pages.a2a.a2ATopics.delegationGrants')}</div><div className="v">{ov.grants.active}</div><div className="s">{tr('pages.a2a.a2ATopics.revoked', { v0: ov.grants.revoked })}</div></div>
        <div className="a2a-tile"><div className="k">{tr('pages.a2a.a2ATopics.auditEvents')}</div><div className="v">{ov.events.total}</div><div className="s">{tr('pages.a2a.a2ATopics.denied', { v0: ov.events.denied })}</div></div>
        <div className="a2a-tile"><div className="k">{tr('pages.a2a.a2ATopics.auditChain')}</div><div className="v" style={{ color: ov.chain.ok ? 'var(--success)' : 'var(--danger)' }}>{ov.chain.ok ? tr('pages.a2a.a2ATopics.intact') : tr('pages.a2a.a2ATopics.broken')}</div><div className="s">{tr('pages.a2a.a2ATopics.eventsVerified', { v0: ov.chain.rowsChecked })}</div></div>
      </div>}

      <div className="ent-card"><div className="ent-card-head"><Icon name="share" /> {tr('pages.a2a.a2ATopics.topicDomains')}</div><div className="ent-card-body">
        {topics.length === 0 ? <div className="ent-empty">{tr('pages.a2a.a2ATopics.noTopicsYetCreateOneTo')}</div> : (
          <div className="ent-table-wrap"><table className="ent-table"><thead><tr><th>{tr('pages.a2a.a2ATopics.topic')}</th><th>{tr('pages.a2a.a2ATopics.status')}</th><th>{tr('pages.a2a.a2ATopics.agents')}</th><th>{tr('pages.a2a.a2ATopics.liveGrants')}</th><th>{tr('pages.a2a.a2ATopics.createdBy')}</th><th /></tr></thead><tbody>
            {topics.map((t) => (
              <tr key={t.topic_id} style={{ cursor: 'pointer' }} onClick={() => nav(`/a2a/topics/${t.topic_id}`)}>
                <td><strong>{t.name}</strong><div className="a2a-muted">{t.mission}</div></td>
                <td><span className={`badge ${statusBadge(t.status)}`}>{t.status.replace('_', ' ')}</span></td>
                <td>{t.member_count}</td><td>{t.active_grants}</td>
                <td>{t.created_by}<div className="a2a-muted">{parseServerDate(t.created_at).toLocaleString()}</div></td>
                <td onClick={(e) => e.stopPropagation()}>{canWrite && t.status === 'pending_approval' && <button className="ent-btn sm primary" onClick={() => approve(t)}><Icon name="checkCircle" /> {tr('pages.a2a.a2ATopics.approve')}</button>}</td>
              </tr>))}
          </tbody></table></div>)}
      </div></div>
      {wizard && <TopicWizard apiUrl={apiUrl} onClose={() => setWizard(false)} onCreated={(id) => { setWizard(false); setNote('Topic submitted — a second administrator must approve it.'); void load(); nav(`/a2a/topics/${id}`); }} />}
    </div>
  );
};
export default A2ATopics;
